import { PageLayout } from 'src/layouts/page'
import { Breadcrumb, Card, Text, Tooltip, Skeleton, LinkButton } from '$components'
import { BinIcon, EditIcon, PersonCircleIcon } from '$components'
import { ElementIntent } from '$components'
import { formatDate } from '$components'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { TeamRoutePath } from '../routes/protected'
import { OptionsDropdown } from '$components'
import { ConfirmationModal } from '$components'
import { ImageCircle } from '$components'
import { scheduleShowOptions, userDeleteMutation, userShowOptions } from '$api-client/@tanstack/react-query.gen'
import { TeamMemberWorkingHours } from '../components/team-member-working-times'
import { Role } from '$api-client/types.gen'
import { PermissionsHelper } from '$utils/permissions'
import { useAuth } from '$features/auth/hooks/use-auth'
import { useModalState } from '$components'
import { BreadcrumbSkeleton } from '$components'
import { ProfileSkeleton } from '$components'
import { InfoSkeleton } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { Router } from '$utils/router'
import { ToastHelper } from '$components'
import { ToastType } from '$components'
import { usePageTitle } from '$components'

export function TeamMemberPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const { hasSubscriptionExpired } = useTenant()

  const {
    open: openTeamMemberDeleteConfirmationModal,
    close: closeTeamMemberDeleteConfirmationModal,
    modalState: teamMemberDeleteConfirmationModalState,
  } = useModalState()

  const { data, isLoading } = useQuery({
    ...userShowOptions({
      path: {
        id: Number(id),
      },
      query: {
        include_deleted: true,
      },
    }),
    enabled: !!id,
  })

  const { data: schedule, isLoading: isLoadingSchedule } = useQuery({
    ...scheduleShowOptions({
      path: {
        userId: Number(id),
      },
      query: {
        include_deleted: true,
      },
    }),
  })

  const { mutate: deleteTeamMember, isPending: isDeletingTeamMember } = useMutation({
    ...userDeleteMutation(),
    onSuccess: () => {
      ToastHelper.deleteSuccess(ToastType.TeamMember)
      navigate(TeamRoutePath.Team)
    },
    onError: () => ToastHelper.deleteError(ToastType.TeamMember),
  })

  usePageTitle(data?.data ? `${data.data.first_name} ${data.data.last_name}` : 'Team Member')

  const handleConfirmDelete = () => {
    deleteTeamMember({
      path: {
        id: Number(id),
      },
    })
  }

  if (isLoading) {
    return (
      <PageLayout>
        <div className="flex flex-col gap-6 w-full">
          <BreadcrumbSkeleton />

          {/* Header Skeleton */}
          <div className="flex items-center gap-4">
            <ProfileSkeleton
              avatarSize={64}
              titleWidth={160}
              subtitleWidth={120}
            />
            <div className="flex items-center gap-2 ml-auto">
              <Skeleton
                width={36}
                height={36}
                borderRadius={10}
              />
            </div>
          </div>

          {/* Team Member Information Card Skeleton */}
          <Card
            rounding="xl"
            padding="none"
          >
            <Card.Header
              padding="sm"
              className="flex justify-between items-center"
            >
              <Skeleton
                width={80}
                height={20}
                borderRadius={7}
              />
            </Card.Header>

            <Card.Content
              padding="sm"
              className="flex flex-col gap-4"
            >
              <InfoSkeleton
                labelWidth={50}
                contentWidth={200}
              />
              <InfoSkeleton
                labelWidth={50}
                contentWidth={200}
              />
            </Card.Content>
          </Card>

          {/* Working Hours Skeleton */}
          <Card
            rounding="xl"
            padding="none"
          >
            <Card.Header
              padding="sm"
              className="flex justify-between items-center"
            >
              <Skeleton
                width={120}
                height={20}
                borderRadius={7}
              />
            </Card.Header>

            <Card.Content
              padding="sm"
              className="flex flex-col gap-4"
            >
              {Array.from({ length: 7 }).map((_, index) => (
                <div
                  key={index}
                  className="grid grid-cols-2 gap-2"
                >
                  <Skeleton
                    width={100}
                    height={18}
                    borderRadius={6}
                  />

                  <Skeleton
                    width={100}
                    height={18}
                    borderRadius={6}
                  />
                </div>
              ))}
            </Card.Content>
          </Card>
        </div>
      </PageLayout>
    )
  }

  if (!data) {
    return null
  }

  const user = data.data

  const isProfileEditDisabled = hasSubscriptionExpired || PermissionsHelper.doesntHaveRole(authUser.role, [Role.SUPER_ADMIN, Role.ADMIN])
  const isDetailsEditDisabled = hasSubscriptionExpired || user.role === Role.SUPER_ADMIN || PermissionsHelper.doesntHaveRole(authUser.role, [Role.SUPER_ADMIN, Role.ADMIN])

  const getProfileEditTooltipContent = () => {
    if (hasSubscriptionExpired) {return SUBSCRIPTION_EXPIRED_MESSAGE}
    if (PermissionsHelper.doesntHaveRole(authUser.role, [Role.SUPER_ADMIN, Role.ADMIN])) {return 'You are not allowed to edit users.'}
    return undefined
  }

  const getDetailsEditTooltipContent = () => {
    if (hasSubscriptionExpired) {return SUBSCRIPTION_EXPIRED_MESSAGE}
    if (user.role === Role.SUPER_ADMIN) {return 'Organisation owner\'s role cannot be edited.'}
    if (PermissionsHelper.doesntHaveRole(authUser.role, [Role.SUPER_ADMIN, Role.ADMIN])) {return 'You are not allowed to edit users.'}
    return undefined
  }

  return (
    <PageLayout>
      {/* Main Content */}
      <div className="flex flex-col gap-6 w-full pb-4">
        <Breadcrumb
          items={[
            {
              label: 'Team',
              path: TeamRoutePath.Team,
              icon: PersonCircleIcon,
            },
            {
              label: `${user.first_name} ${user.last_name}`,
              path: Router.getRoute(TeamRoutePath.TeamMemberView, { id: user.id }),
            },
          ]}
        />

        {/* Header */}
        <div className="flex items-center gap-4">
          <ImageCircle
            image={user.image}
            size="2xl"
            outline="neutral"
          />

          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <Text
                as="h1"
                type="h2"
              >
                {`${user.first_name} ${user.last_name}`}
              </Text>

              {
                user.updated_at && (
                  <Text
                    type="s"
                    className="text-gray-600"
                  >
                    Last updated on
                    &nbsp;
                    {formatDate(user.updated_at)}
                  </Text>
                )
              }
            </div>

            <Tooltip
              content={getProfileEditTooltipContent()}
              disabled={!isProfileEditDisabled}
            >
              <LinkButton
                variant="secondary"
                size="sm"
                to={TeamRoutePath.TeamMemberEditProfile}
                icon={EditIcon}
                disabled={isProfileEditDisabled}
              >
                Edit
              </LinkButton>
            </Tooltip>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <OptionsDropdown options={[
              {
                id: 'delete',
                value: 'Delete',
                onClick: () => openTeamMemberDeleteConfirmationModal(user.id),
                intent: ElementIntent.Danger,
                icon: BinIcon,
                disabled: PermissionsHelper.doesntHaveRole(authUser.role, [Role.SUPER_ADMIN, Role.ADMIN])
                  || user.role === Role.SUPER_ADMIN
                  || !!user.deleted_at,
                tooltip: PermissionsHelper.doesntHaveRole(authUser.role, [Role.SUPER_ADMIN, Role.ADMIN])
                  ? 'You are not allowed to delete users.'
                  : user.role === Role.SUPER_ADMIN
                    ? 'Owner of the organisation cannot be deleted.'
                    : user.deleted_at !== null
                      ? 'This user has already been deleted.'
                      : undefined,
              },
            ]}
            />
          </div>
        </div>

        {/* Team Member Information */}
        <Card
          rounding="xl"
          padding="none"
        >
          <Card.Header
            padding="sm"
            className="flex justify-between items-center"
          >
            <Text type="h4">
              Details
            </Text>
          </Card.Header>

          <Card.Content
            padding="sm"
            className="flex flex-col gap-4"
          >
            <div>
              <Card.Label>Email</Card.Label>
              <Text type="s">
                {user.email}
              </Text>
            </div>

            <div className="flex items-center gap-4">
              <div>
                <Card.Label>Role</Card.Label>
                <Text type="s">
                  {user.role}
                </Text>
              </div>

              <Tooltip
                content={getDetailsEditTooltipContent()}
                disabled={!isDetailsEditDisabled}
              >
                <LinkButton
                  variant="secondary"
                  size="sm"
                  to={TeamRoutePath.TeamMemberEditDetails}
                  icon={EditIcon}
                  disabled={isDetailsEditDisabled}
                >
                  Edit
                </LinkButton>
              </Tooltip>
            </div>
          </Card.Content>
        </Card>

        { !isLoadingSchedule && schedule?.data && (
          <TeamMemberWorkingHours schedule={schedule.data} />
        )}
      </div>

      {teamMemberDeleteConfirmationModalState.isOpen && (
        <ConfirmationModal
          isLoading={isDeletingTeamMember}
          intent={ElementIntent.Danger}
          onClose={closeTeamMemberDeleteConfirmationModal}
          onConfirm={handleConfirmDelete}
          title="Delete Team Member"
          description="Are you sure you want to delete this team member?"
        />
      )}

      <Outlet />
    </PageLayout>
  )
}
